import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import soundtrackUrl from "../../assets/audio/first-snow.mp3";
import { createSoundtrackPlayer, type PlaybackState, type SoundtrackPlayer } from "../../audio/createSoundtrackPlayer";
import { getStorySoundtrackVolume, soundtrackMix } from "../../audio/soundtrackMix";
import { fireworkAudio } from "../../audio/fireworkAudio";

gsap.registerPlugin(useGSAP);

interface SoundtrackProps {
  phase: "intro" | "cinematic" | "story";
  candleExtinguished: boolean;
  finaleReady: boolean;
}

export interface SoundtrackHandle {
  start: () => Promise<void>;
  reset: () => void;
  syncCelebration: (elapsed: number) => void;
}

export const Soundtrack = forwardRef<SoundtrackHandle, SoundtrackProps>(function Soundtrack(
  { phase, candleExtinguished, finaleReady }, ref,
) {
  const rootRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<SoundtrackPlayer | null>(null);
  const userStartedRef = useRef(false);
  const [playbackState, setPlaybackState] = useState<PlaybackState>("idle");

  useGSAP(() => {
    // Each player owns its element, so StrictMode / HMR never reconnects a used audio source.
    const audio = new Audio(soundtrackUrl);
    audio.preload = "metadata";
    audio.hidden = true;
    rootRef.current?.append(audio);
    const player = createSoundtrackPlayer(audio, state => {
      setPlaybackState(state);
      fireworkAudio.setEnabled(state === "playing");
    });
    playerRef.current = player;
    const visibility = () => {
      void player.setHidden(document.hidden);
      if (document.hidden) void fireworkAudio.suspend();
      else if (userStartedRef.current) void fireworkAudio.unlock();
    };
    if (document.hidden) void player.setHidden(true);
    document.addEventListener("visibilitychange", visibility);
    return () => {
      document.removeEventListener("visibilitychange", visibility);
      player.dispose();
      audio.removeAttribute("src");
      audio.load();
      audio.remove();
      if (playerRef.current === player) playerRef.current = null;
      fireworkAudio.dispose();
    };
  }, { scope: rootRef });

  useImperativeHandle(ref, () => ({
    async start() {
      userStartedRef.current = true;
      void fireworkAudio.unlock();
      await playerRef.current?.start();
    },
    reset() { userStartedRef.current = false; playerRef.current?.reset(); },
    syncCelebration(elapsed) { playerRef.current?.syncCelebration(elapsed); },
  }));

  useGSAP(() => {
    const player = playerRef.current;
    if (!player) return;
    if (finaleReady) { player.finish(); return; }
    if (candleExtinguished) return;
    if (phase !== "story") { player.setSceneVolume(soundtrackMix.cinematic); return; }
    const updateVolume = () => {
      const range = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      player.setSceneVolume(getStorySoundtrackVolume(window.scrollY / range));
    };
    updateVolume();
    window.addEventListener("scroll", updateVolume, { passive: true });
    window.addEventListener("resize", updateVolume);
    return () => {
      window.removeEventListener("scroll", updateVolume);
      window.removeEventListener("resize", updateVolume);
    };
  }, { scope: rootRef, dependencies: [phase, candleExtinguished, finaleReady], revertOnUpdate: true });

  const isPlaying = playbackState === "playing";
  const isVisible = playbackState !== "idle";
  return (
    <div ref={rootRef} className="soundtrack">
      <button
        className={`sound-control${isVisible ? " sound-control--visible" : ""}${isPlaying ? " sound-control--playing" : ""}`}
        type="button"
        aria-label={isPlaying ? "关闭声音（音乐与烟花音效）" : "开启声音（音乐与烟花音效）"}
        aria-pressed={isPlaying}
        tabIndex={isVisible ? 0 : -1}
        onClick={() => { if (!isPlaying) void fireworkAudio.unlock(); void playerRef.current?.toggle(); }}
      >
        <svg className="sound-control__icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4.5 9.2h3.2l4.1-3.4v12.4l-4.1-3.4H4.5z" />
          <path className="sound-control__wave sound-control__wave--near" d="M15.1 9a4 4 0 0 1 0 6" />
          <path className="sound-control__wave sound-control__wave--far" d="M17.5 6.7a7.2 7.2 0 0 1 0 10.6" />
        </svg>
        <span>声音</span>
      </button>
    </div>
  );
});
